import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Chip,
  TextField,
  InputAdornment,
  Tabs,
  Tab,
  Stack,
  Divider,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  Alert,
  Breadcrumbs,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from '@mui/material';
import {
  Search as SearchIcon,
  RocketLaunch as StartIcon,
  Stop as EndIcon,
  SmartToy as AiIcon,
  Description as FormIcon,
  Storage as RagIcon,
  Email as EmailIcon,
  Sms as SmsIcon,
  CloudUpload as DatabaseIcon,
  Api as ApiIcon,
  Webhook as WebhookIcon,
  CallSplit as ConditionalIcon,
  Loop as LoopIcon,
  Timer as DelayIcon,
  TouchApp as TriggerIcon,
  CallMerge as MergeIcon,
  Code as CodeIcon,
  Hub as ParallelIcon,
  Security as SecurityIcon,
  Folder as FolderIcon,
  Upload as UploadIcon,
  ArrowForward as ArrowIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Home as HomeIcon,
  Book as BookIcon,
  Category as CategoryIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { nodeDocumentation, edgeDocumentation, clioNodeDocumentation } from '../data/nodeDocumentation';

const NodeDocumentationHub = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  // Node definitions with documentation links
  const nodes = [
    // Control Nodes
    { type: 'start', name: 'Start Node', icon: <StartIcon />, category: 'Control', color: '#4caf50', description: 'Entry point for every workflow', status: 'production' },
    { type: 'end', name: 'End Node', icon: <EndIcon />, category: 'Control', color: '#f44336', description: 'Exit point for workflow completion', status: 'production' },
    
    // Core AI/ML
    { type: 'ai-agent', name: 'AI Agent', icon: <AiIcon />, category: 'AI/ML', color: '#2196f3', description: 'Call LLM for intelligent processing', status: 'production' },
    { type: 'form', name: 'Form', icon: <FormIcon />, category: 'AI/ML', color: '#9c27b0', description: 'Collect user input with structured fields', status: 'production' },
    { type: 'rag', name: 'RAG', icon: <RagIcon />, category: 'AI/ML', color: '#ff9800', description: 'Query knowledge base with AI', status: 'production' },
    
    // Integration
    { type: 'email', name: 'Email', icon: <EmailIcon />, category: 'Integration', color: '#f44336', description: 'Send emails with templates', status: 'production' },
    { type: 'sms', name: 'SMS', icon: <SmsIcon />, category: 'Integration', color: '#4caf50', description: 'Send text messages', status: 'production' },
    { type: 'database', name: 'Database', icon: <DatabaseIcon />, category: 'Integration', color: '#607d8b', description: 'Query or modify MongoDB data', status: 'production' },
    { type: 'api-call', name: 'API Call', icon: <ApiIcon />, category: 'Integration', color: '#00bcd4', description: 'Make HTTP requests to external services', status: 'production' },
    { type: 'webhook', name: 'Webhook', icon: <WebhookIcon />, category: 'Integration', color: '#009688', description: 'Create HTTP endpoints', status: 'production' },
    
    // Processing
    { type: 'conditional', name: 'Conditional', icon: <ConditionalIcon />, category: 'Processing', color: '#ff9800', description: 'Branch based on conditions', status: 'production' },
    { type: 'loop', name: 'Loop', icon: <LoopIcon />, category: 'Processing', color: '#e91e63', description: 'Repeat actions for multiple items', status: 'production' },
    { type: 'delay', name: 'Delay', icon: <DelayIcon />, category: 'Processing', color: '#9e9e9e', description: 'Wait before continuing', status: 'production' },
    { type: 'trigger', name: 'Trigger', icon: <TriggerIcon />, category: 'Processing', color: '#ff5722', description: 'Start workflows automatically', status: 'production' },
    { type: 'merge', name: 'Merge', icon: <MergeIcon />, category: 'Processing', color: '#3f51b5', description: 'Combine data from parallel branches', status: 'production' },
    { type: 'code-executor', name: 'Code Executor', icon: <CodeIcon />, category: 'Processing', color: '#795548', description: 'Run custom code', status: 'production' },
    
    // Control Flow
    { type: 'parallel', name: 'Parallel', icon: <ParallelIcon />, category: 'Control Flow', color: '#673ab7', description: 'Execute multiple paths simultaneously', status: 'production' },
    
    // Legal (Clio)
    { type: 'clio-conflict-check', name: 'Conflict Check', icon: <SecurityIcon />, category: 'Legal', color: '#1976d2', description: 'Check conflicts in Clio', status: 'production' },
    { type: 'clio-create-matter', name: 'Create Matter', icon: <FolderIcon />, category: 'Legal', color: '#1976d2', description: 'Create legal matter in Clio', status: 'production' },
    { type: 'clio-post-time', name: 'Post Time', icon: <DelayIcon />, category: 'Legal', color: '#1976d2', description: 'Log billable hours to Clio', status: 'production' },
    { type: 'clio-upload-document', name: 'Upload Document', icon: <UploadIcon />, category: 'Legal', color: '#1976d2', description: 'Upload documents to Clio', status: 'production' },
  ];

  const edges = [
    { type: 'edge-basic', name: 'Basic Edge', icon: <ArrowIcon />, color: '#607d8b', description: 'Simple sequential connection', status: 'production' },
    { type: 'edge-conditional', name: 'Conditional Edge', icon: <ConditionalIcon />, color: '#ff9800', description: 'Branch based on conditions', status: 'production' },
    { type: 'edge-success', name: 'Success Edge', icon: <CheckIcon />, color: '#4caf50', description: 'Execute on success', status: 'production' },
    { type: 'edge-error', name: 'Error Edge', icon: <ErrorIcon />, color: '#f44336', description: 'Execute on error/failure', status: 'production' },
  ];

  // Filter nodes based on search and category
  const filteredNodes = nodes.filter(node => {
    const matchesSearch = node.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         node.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         node.type.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || node.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const categories = ['all', 'Control', 'AI/ML', 'Integration', 'Processing', 'Control Flow', 'Legal'];

  const [selectedNode, setSelectedNode] = useState(null);
  const [selectedEdge, setSelectedEdge] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const handleNodeClick = (node) => {
    setSelectedNode(node);
    setSelectedEdge(null);
    setDialogOpen(true);
  };

  const handleEdgeClick = (edge) => {
    setSelectedEdge(edge);
    setSelectedNode(null);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  // Get documentation for selected node
  const getNodeDoc = () => {
    if (!selectedNode) return null;
    return nodeDocumentation[selectedNode.type] || clioNodeDocumentation[selectedNode.type] || null;
  };

  const getEdgeDoc = () => {
    if (!selectedEdge) return null;
    return edgeDocumentation[selectedEdge.type] || null;
  };

  const currentDoc = selectedNode ? getNodeDoc() : getEdgeDoc();

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#fafafa' }}>
      {/* Header */}
      <Box sx={{
        bgcolor: 'primary.main',
        color: 'white',
        py: 6,
        mb: 4,
      }}>
        <Container maxWidth="lg">
          <Breadcrumbs sx={{ color: 'rgba(255,255,255,0.7)', mb: 2 }}>
            <Link color="inherit" href="/" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <HomeIcon fontSize="small" />
              Home
            </Link>
            <Typography color="white">Node & Edge Documentation</Typography>
          </Breadcrumbs>
          
          <Typography variant="h3" gutterBottom sx={{ fontWeight: 'bold' }}>
            🎨 Node & Edge Documentation Hub
          </Typography>
          <Typography variant="h6" sx={{ opacity: 0.9, mb: 3 }}>
            Complete reference for all 21 node types and 4 edge types
          </Typography>
          
          <Stack direction="row" spacing={2} flexWrap="wrap" gap={1}>
            <Chip label="21 Nodes" sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 600 }} />
            <Chip label="4 Edge Types" sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 600 }} />
            <Chip label="100% Documented" sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 600 }} />
            <Chip label="Production Ready" sx={{ bgcolor: '#4caf50', color: 'white', fontWeight: 600 }} />
          </Stack>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ pb: 8 }}>
        {/* Quick Links */}
        <Paper sx={{ p: 3, mb: 4 }}>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <BookIcon />
            Quick Links
          </Typography>
          <Stack direction="row" spacing={2} flexWrap="wrap" gap={2} sx={{ mt: 2 }}>
            <Button 
              variant="outlined" 
              size="small" 
              onClick={() => navigate('/agentFlowBuilder')}
            >
              Open Flow Builder
            </Button>
            <Button 
              variant="outlined" 
              size="small"
              onClick={() => {
                alert('📚 Node Documentation\n\nAll 21 nodes are documented on this page!\n\nUse the search and filter features below to explore:\n\n• Search by name (e.g., "email", "ai", "conditional")\n• Filter by category (Control, AI/ML, Integration, Processing, Legal)\n• Click any node card to see a quick description\n\nEach node shows:\n• Icon and color\n• Description\n• Category\n• Type\n• Status');
              }}
            >
              How to Use This Page
            </Button>
          </Stack>
        </Paper>

        {/* Search and Filters */}
        <Paper sx={{ p: 3, mb: 4 }}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                placeholder="Search nodes... (e.g., email, ai, conditional)"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Tabs
                value={selectedCategory}
                onChange={(e, newValue) => setSelectedCategory(newValue)}
                variant="scrollable"
                scrollButtons="auto"
              >
                {categories.map((cat) => (
                  <Tab
                    key={cat}
                    label={cat === 'all' ? 'All Nodes' : cat}
                    value={cat}
                  />
                ))}
              </Tabs>
            </Grid>
          </Grid>
        </Paper>

        {/* Statistics */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={6} sm={3}>
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="h4" color="primary" sx={{ fontWeight: 'bold' }}>21</Typography>
              <Typography variant="body2" color="text.secondary">Total Nodes</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="h4" color="success.main" sx={{ fontWeight: 'bold' }}>21</Typography>
              <Typography variant="body2" color="text.secondary">Production Ready</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="h4" color="primary" sx={{ fontWeight: 'bold' }}>4</Typography>
              <Typography variant="body2" color="text.secondary">Edge Types</Typography>
            </Paper>
          </Grid>
          <Grid item xs={6} sm={3}>
            <Paper sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="h4" color="success.main" sx={{ fontWeight: 'bold' }}>100%</Typography>
              <Typography variant="body2" color="text.secondary">Documented</Typography>
            </Paper>
          </Grid>
        </Grid>

        {/* Nodes Grid */}
        <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
          <CategoryIcon />
          Node Types
          <Chip label={`${filteredNodes.length} nodes`} size="small" color="primary" />
        </Typography>

        <Grid container spacing={3} sx={{ mb: 6 }}>
          {filteredNodes.map((node) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={node.type}>
              <Card
                sx={{
                  height: '100%',
                  cursor: 'pointer',
                  transition: 'all 0.3s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 6,
                  },
                }}
              >
                <CardActionArea onClick={() => handleNodeClick(node)} sx={{ height: '100%' }}>
                  <CardContent>
                    <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      mb: 2,
                    }}>
                      <Box sx={{
                        bgcolor: node.color,
                        color: 'white',
                        p: 1.5,
                        borderRadius: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}>
                        {node.icon}
                      </Box>
                      <Chip
                        label={node.status === 'production' ? 'Ready' : 'Beta'}
                        size="small"
                        color={node.status === 'production' ? 'success' : 'warning'}
                      />
                    </Box>
                    
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                      {node.name}
                    </Typography>
                    
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2, minHeight: 60 }}>
                      {node.description}
                    </Typography>
                    <Typography variant="caption" color="primary" sx={{ display: 'block', mb: 2, fontStyle: 'italic' }}>
                      Type: {node.type}
                    </Typography>
                    
                    <Chip
                      label={node.category}
                      size="small"
                      variant="outlined"
                      sx={{ borderColor: node.color, color: node.color }}
                    />
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>

        {filteredNodes.length === 0 && (
          <Alert severity="info" sx={{ mb: 4 }}>
            No nodes found matching &quot;{searchTerm}&quot;. Try a different search term or category.
          </Alert>
        )}

        {/* Edge Types */}
        <Divider sx={{ my: 6 }} />
        
        <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
          <ArrowIcon />
          Edge Types
          <Chip label="4 edge types" size="small" color="primary" />
        </Typography>

        <Grid container spacing={3}>
          {edges.map((edge) => (
            <Grid item xs={12} sm={6} md={3} key={edge.type}>
              <Card
                sx={{
                  cursor: 'pointer',
                  transition: 'all 0.3s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 6,
                  },
                }}
              >
                <CardActionArea onClick={() => handleEdgeClick(edge)}>
                  <CardContent>
                    <Box sx={{
                      bgcolor: edge.color,
                      color: 'white',
                      p: 1.5,
                      borderRadius: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: 2,
                    }}>
                      {edge.icon}
                    </Box>
                    
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                      {edge.name}
                    </Typography>
                    
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {edge.description}
                    </Typography>
                    
                    <Chip
                      label="Ready"
                      size="small"
                      color="success"
                    />
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Additional Resources */}
        <Paper sx={{ p: 4, mt: 6, bgcolor: '#e3f2fd' }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
            📚 Additional Resources
          </Typography>
          <Grid container spacing={3} sx={{ mt: 2 }}>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>Documentation</Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon><BookIcon color="primary" /></ListItemIcon>
                  <ListItemText
                    primary="Comprehensive Node Guide"
                    secondary="In-depth guide for all nodes"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><TriggerIcon color="primary" /></ListItemIcon>
                  <ListItemText
                    primary="Trigger Node Deep Dive"
                    secondary="Complete guide to event-based triggers"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><BookIcon color="primary" /></ListItemIcon>
                  <ListItemText
                    primary="API Documentation"
                    secondary="REST API reference"
                  />
                </ListItem>
              </List>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>Example Workflows</Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon><SecurityIcon color="primary" /></ListItemIcon>
                  <ListItemText
                    primary="Legal Workflows"
                    secondary="3 workflows for law firms"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><EmailIcon color="primary" /></ListItemIcon>
                  <ListItemText
                    primary="Medical Workflows"
                    secondary="5 workflows for healthcare"
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon><HomeIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary="Real Estate Workflows"
                    secondary="5 workflows for agents"
                  />
                </ListItem>
              </List>
            </Grid>
          </Grid>
        </Paper>

        {/* Footer CTA */}
        <Box sx={{ textAlign: 'center', mt: 6 }}>
          <Typography variant="h5" gutterBottom>
            Ready to build your workflow?
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate('/agentic-flow-builder')}
            startIcon={<StartIcon />}
            sx={{ mt: 2 }}
          >
            Open Flow Builder
          </Button>
        </Box>
      </Container>

      {/* Documentation Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: '90vh'
          }
        }}
      >
        {selectedNode && currentDoc && (
          <>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: selectedNode.color, color: 'white' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {selectedNode.icon}
                <Box>
                  <Typography variant="h6">{selectedNode.name}</Typography>
                  <Typography variant="caption">Type: {selectedNode.type}</Typography>
                </Box>
              </Box>
              <IconButton onClick={handleCloseDialog} sx={{ color: 'white' }}>
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers>
              {/* Overview */}
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>Overview</Typography>
              <Typography variant="body1" paragraph>{currentDoc.overview}</Typography>

              {/* When to Use */}
              <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>When to Use</Typography>
              <List dense>
                {currentDoc.whenToUse && currentDoc.whenToUse.map((use, idx) => (
                  <ListItem key={idx}>
                    <ListItemIcon><CheckIcon color="success" fontSize="small" /></ListItemIcon>
                    <ListItemText primary={use} />
                  </ListItem>
                ))}
              </List>

              {/* Configuration */}
              {currentDoc.configuration && (
                <>
                  <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Configuration</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="subtitle2" color="error">Required Fields:</Typography>
                      <List dense>
                        {currentDoc.configuration.required.map((field, idx) => (
                          <ListItem key={idx} sx={{ py: 0.5 }}>
                            <Typography variant="body2">• {field}</Typography>
                          </ListItem>
                        ))}
                      </List>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="subtitle2" color="primary">Optional Fields:</Typography>
                      <List dense>
                        {currentDoc.configuration.optional.map((field, idx) => (
                          <ListItem key={idx} sx={{ py: 0.5 }}>
                            <Typography variant="body2">• {field}</Typography>
                          </ListItem>
                        ))}
                      </List>
                    </Grid>
                  </Grid>
                </>
              )}

              {/* Example */}
              {currentDoc.example && (
                <>
                  <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Example Configuration</Typography>
                  <Paper sx={{ p: 2, bgcolor: '#f5f5f5', fontFamily: 'monospace', fontSize: '0.85rem', overflow: 'auto' }}>
                    <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{currentDoc.example}</pre>
                  </Paper>
                </>
              )}

              {/* Best Practices */}
              {currentDoc.bestPractices && (
                <>
                  <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Best Practices</Typography>
                  <List dense>
                    {currentDoc.bestPractices.map((practice, idx) => (
                      <ListItem key={idx}>
                        <Typography variant="body2">{practice}</Typography>
                      </ListItem>
                    ))}
                  </List>
                </>
              )}

              {/* Common Patterns */}
              {currentDoc.commonPatterns && (
                <>
                  <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Common Patterns</Typography>
                  <List dense>
                    {currentDoc.commonPatterns.map((pattern, idx) => (
                      <ListItem key={idx}>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>
                          {pattern}
                        </Typography>
                      </ListItem>
                    ))}
                  </List>
                </>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => navigate('/agentic-flow-builder')} variant="outlined">
                Open Flow Builder
              </Button>
              <Button onClick={handleCloseDialog} variant="contained">
                Close
              </Button>
            </DialogActions>
          </>
        )}

        {selectedEdge && currentDoc && (
          <>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: selectedEdge.color, color: 'white' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {selectedEdge.icon}
                <Box>
                  <Typography variant="h6">{selectedEdge.name}</Typography>
                  <Typography variant="caption">Type: {selectedEdge.type}</Typography>
                </Box>
              </Box>
              <IconButton onClick={handleCloseDialog} sx={{ color: 'white' }}>
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers>
              <Typography variant="h6" gutterBottom>Overview</Typography>
              <Typography variant="body1" paragraph>{currentDoc.overview}</Typography>

              <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>When to Use</Typography>
              <List dense>
                {currentDoc.whenToUse && currentDoc.whenToUse.map((use, idx) => (
                  <ListItem key={idx}>
                    <ListItemIcon><CheckIcon color="success" fontSize="small" /></ListItemIcon>
                    <ListItemText primary={use} />
                  </ListItem>
                ))}
              </List>

              {currentDoc.example && (
                <>
                  <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Example</Typography>
                  <Paper sx={{ p: 2, bgcolor: '#f5f5f5', fontFamily: 'monospace', fontSize: '0.85rem' }}>
                    <pre style={{ margin: 0 }}>{currentDoc.example}</pre>
                  </Paper>
                </>
              )}

              {currentDoc.visualStyle && (
                <>
                  <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>Visual Style</Typography>
                  <Typography variant="body1">{currentDoc.visualStyle}</Typography>
                </>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseDialog} variant="contained">
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default NodeDocumentationHub;

